import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save } from "lucide-react";

export default function AdminSettings() {
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/admin/settings"); setS(data); }
    catch (e) { if (e?.response?.status === 401) nav("/admin/login"); }
  })(); /* eslint-disable-next-line */ }, []);
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings", s); toast.success("Settings saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  if (!s) return <div className="pt-32 text-center">Loading…</div>;
  const set = (k, v) => setS({ ...s, [k]: v });
  const setNested = (k1, k2, v) => setS({ ...s, [k1]: { ...s[k1], [k2]: v } });
  const F = (label, key, testid, type = "text") => (
    <label className="block text-sm">
      <span className="text-xs text-slate-500 uppercase">{label}</span>
      <input type={type} data-testid={testid} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" value={s[key] || ""} onChange={(e) => set(key, type === "number" ? parseFloat(e.target.value) || 0 : e.target.value)} />
    </label>
  );
  const TA = (label, key, testid) => (
    <label className="block text-sm">
      <span className="text-xs text-slate-500 uppercase">{label}</span>
      <textarea rows={5} data-testid={testid} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-xs" value={s[key] || ""} onChange={(e) => set(key, e.target.value)} />
    </label>
  );
  return (
    <div className="pt-24 pb-16 max-w-4xl mx-auto px-4 space-y-6">
      <h1 className="text-3xl font-bold text-slate-900">Business Settings</h1>
      <p className="text-slate-600">These values appear on claim checks, invoices, and public pages. Some fields must be verified with California BHGS, CDTFA, or an attorney before public use.</p>
      <section className="p-5 rounded-2xl border border-slate-200 bg-white space-y-3">
        <h2 className="text-lg font-bold">Business identity</h2>
        {F("Legal business name", "legal_business_name", "s-legal")}
        {F("DBA", "dba", "s-dba")}
        {F("Address", "address", "s-address")}
        {F("Phone", "phone", "s-phone")}
        {F("Email", "email", "s-email")}
        {F("Website", "website", "s-website")}
        {F("California Electronic Service Dealer registration number", "ca_esd_registration", "s-esd")}
        {F("Seller's permit info (internal only)", "sellers_permit", "s-permit")}
      </section>
      <section className="p-5 rounded-2xl border border-amber-200 bg-amber-50/60 space-y-3">
        <h2 className="text-lg font-bold text-amber-900">California statutory claim-check notice</h2>
        <p className="text-xs text-amber-800">Enter the exact current notice required by California BHGS. Verify against current BHGS requirements before production use. This text is stamped verbatim onto every generated claim check.</p>
        {TA("California Required Claim Check Notice", "california_claim_check_notice", "s-ca-notice")}
      </section>
      <section className="p-5 rounded-2xl border border-slate-200 bg-white space-y-3">
        <h2 className="text-lg font-bold">Tax</h2>
        <label className="block text-sm">
          <span className="text-xs text-slate-500 uppercase">Sales tax mode</span>
          <select data-testid="s-tax-mode" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
            value={s.tax_configuration?.mode || "diy"} onChange={(e) => setNested("tax_configuration", "mode", e.target.value)}>
            <option value="diy">DIY (I set the rate)</option>
            <option value="stripe_calc">Stripe calculates for me</option>
            <option value="none">No tax collected</option>
          </select>
        </label>
        <label className="block text-sm">
          <span className="text-xs text-slate-500 uppercase">Tax rate %</span>
          <input type="number" step="0.01" data-testid="s-tax-rate" className="mt-1 w-32 rounded-lg border border-slate-200 px-3 py-2" value={s.tax_configuration?.rate_percent || 0} onChange={(e) => setNested("tax_configuration", "rate_percent", parseFloat(e.target.value) || 0)} />
        </label>
        <label className="block text-sm">
          <span className="text-xs text-slate-500 uppercase">Display</span>
          <select data-testid="s-tax-display" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" value={s.tax_configuration?.display || "estimated"} onChange={(e) => setNested("tax_configuration", "display", e.target.value)}>
            <option value="estimated">Estimated</option>
            <option value="final">Final</option>
          </select>
        </label>
      </section>
      <section className="p-5 rounded-2xl border border-slate-200 bg-white space-y-3">
        <h2 className="text-lg font-bold">Warranty (90-day default)</h2>
        <p className="text-xs text-slate-500">These fields feed the /policy/warranty page. Owner must complete before publishing.</p>
        <label className="block text-sm"><span className="text-xs text-slate-500 uppercase">Days</span>
          <input type="number" data-testid="s-warranty-days" className="mt-1 w-32 rounded-lg border border-slate-200 px-3 py-2" value={s.warranty?.days || 90} onChange={(e) => setNested("warranty", "days", parseInt(e.target.value, 10) || 90)} /></label>
        <label className="block text-sm"><span className="text-xs text-slate-500 uppercase">Covered</span>
          <textarea rows={3} data-testid="s-warranty-covered" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" value={s.warranty?.covered || ""} onChange={(e) => setNested("warranty", "covered", e.target.value)} /></label>
        <label className="block text-sm"><span className="text-xs text-slate-500 uppercase">Exclusions</span>
          <textarea rows={3} data-testid="s-warranty-excluded" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" value={s.warranty?.excluded || ""} onChange={(e) => setNested("warranty", "excluded", e.target.value)} /></label>
        <label className="block text-sm"><span className="text-xs text-slate-500 uppercase">Remedy</span>
          <textarea rows={3} data-testid="s-warranty-remedy" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" value={s.warranty?.remedy || ""} onChange={(e) => setNested("warranty", "remedy", e.target.value)} /></label>
      </section>
      <section className="p-5 rounded-2xl border border-slate-200 bg-white space-y-3">
        <h2 className="text-lg font-bold">Policies (draft copy)</h2>
        {TA("Parts order & cancellation policy", "parts_cancellation_policy", "s-parts-cancel")}
        {TA("Appointment / no-show policy", "appointment_policy", "s-appt-policy")}
      </section>
      <section className="p-5 rounded-2xl border border-slate-200 bg-white space-y-3">
        <h2 className="text-lg font-bold">Recordkeeping</h2>
        {F("Invoice prefix", "invoice_prefix", "s-inv-prefix")}
        {F("Claim check prefix", "claim_prefix", "s-claim-prefix")}
        {F("Retention (years)", "retention_years", "s-retention", "number")}
        {F("Estimate expiration (days)", "estimate_expiration_days", "s-est-exp", "number")}
      </section>
      <button data-testid="settings-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 disabled:opacity-40">
        <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save settings"}
      </button>
    </div>
  );
}
